import { REVIEW_STEP_LABELS, REVIEW_STEPS, ROUTE_LABELS, type SchemaSnapshot, turnAtTime } from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CheckCheck, FileSearch, Lock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { CandidateCard } from '../components/review/candidate';
import { ChecksStep } from '../components/review/checks-step';
import { ClaimsStep } from '../components/review/claims-step';
import { CompareStep } from '../components/review/compare-step';
import { SkipReviewButton } from '../components/review/inbox';
import { DocumentSamePerson } from '../components/review/same-person';
import { SourceViewer, type ViewerEntity, type ViewerTimeline } from '../components/review/source-viewer';
import { usePlayback } from '../components/transcript/playback';
import { TranscriptPlayer } from '../components/transcript/player';
import { SpeakerMapping } from '../components/transcript/speaker-mapping';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { markProps } from '../lib/entities';
import {
  type ReviewCandidate,
  type ReviewWorkspace,
  useAdopt,
  useCheckWrite,
  useClaimWrite,
  useReviewChecks,
  useReviewClaims,
  useReviewComparison,
  useReviewText,
  useReviewWorkspace,
  useReviewWrite,
} from '../lib/review';
import { useSchema } from '../lib/schema';
import { useCitations, useTranscript } from '../lib/transcripts';

// Review workspace, step 1 · Entity check (PLAN §6, §12.7 screen 4; task 18). The source text on the left with every
// extracted entity's evidence highlighted; on the right the entities grouped by template block (profile sheets) or
// by section, each with its decision: match an existing entity, create new (type and names fixed), same as another
// entity of the document, or reject. Save and come back any time; nothing reaches the graph until review completes.

export const Route = createFileRoute('/review/$id')({ component: ReviewWorkspacePage });

const TOUR = {
  id: 'review',
  steps: [
    {
      target: '[data-tour="review-source"]',
      title: 'The source, with its evidence',
      body: 'Every entity the AI found is underlined where the document names it. Pick an entity on the right to highlight its mentions and step through them.',
      learnMore: 'review-an-upload',
    },
    {
      target: '[data-tour="review-groups"]',
      title: 'Grouped by block',
      body: 'Entities are grouped by the block or section where they first appear. "Accept suggested" decides the safe ones in a block at once.',
    },
    {
      target: '[data-tour="review-progress"]',
      title: 'Save and come back',
      body: 'Each decision is saved as you make it. Continue to facts and relations once every entity has a decision.',
    },
  ],
};

function ReviewWorkspacePage() {
  useScreenTour(TOUR);
  const { id } = Route.useParams();
  const ws = useReviewWorkspace(id);
  const { data: snap } = useSchema();
  const text = useReviewText(id, !!ws.data?.document.hasText);
  if (ws.isError)
    return (
      <EmptyState
        icon={FileSearch}
        title={ws.error instanceof ApiError && ws.error.status === 404 ? 'Nothing to review here' : 'The review could not be loaded'}
        learnMore="review-an-upload"
        action={
          <Link to="/review" className="text-sm underline underline-offset-2">
            Back to the review queue
          </Link>
        }
      >
        {ws.error instanceof ApiError ? ws.error.message : 'Try again in a moment.'}
      </EmptyState>
    );
  if (!ws.data || !snap) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return <Workspace ws={ws.data} snap={snap} text={text.data ?? null} textError={text.isError} />;
}

function Workspace({
  ws,
  snap,
  text,
  textError,
}: {
  ws: ReviewWorkspace;
  snap: SchemaSnapshot;
  text: { text: string; truncated: boolean } | null;
  textError: boolean;
}) {
  const navigate = useNavigate();
  const write = useReviewWrite(ws.document.id);
  const [selected, setSelected] = useState<string | null>(null);
  const [mention, setMention] = useState(0);
  const byId = useMemo(() => new Map(ws.entities.map(e => [e.id, e])), [ws.entities]);
  const viewerEntities = useMemo<ViewerEntity[]>(
    () =>
      ws.entities.map(e => {
        const type = snap.types.find(t => t.key === (e.resolution?.action === 'new' ? e.resolution.typeKey : e.typeKey));
        const m = type ? markProps(snap, { typeId: type.id, kind: type.kind, reviewStatus: 'unreviewed', sensitivity: 'standard' }) : null;
        return { id: e.id, name: e.name, colorSlot: m?.colorSlot ?? null, mentions: e.mentions };
      }),
    [ws.entities, snap],
  );
  const select = (cid: string) => {
    setSelected(s => (s === cid ? null : cid));
    setMention(0);
  };
  const { total, pending } = ws.progress;
  const done = ws.step1.status === 'done';
  const finished = ws.step2.status === 'done';
  const checked = ws.step3.status === 'done';
  // The step on screen: the first one not done (step 3 once the facts are written); earlier steps stay reachable.
  const [view, setView] = useState<1 | 2 | 3>(finished ? 3 : done ? 2 : 1);
  // Transcripts (task 26): the recording plays in sync with the text, and the speakers are mapped here too.
  const transcript = useTranscript(ws.document.id, ws.document.hasTranscript);
  const canPlay = !!transcript.data?.audio.length;
  const play = usePlayback(null, canPlay);
  const timeline = useMemo<ViewerTimeline | undefined>(() => {
    const t = transcript.data;
    if (!t) return undefined;
    return {
      turns: t.turns,
      active: play.currentMs === null ? -1 : turnAtTime(t.turns, play.currentMs),
      onPlay: canPlay ? play.pick : undefined,
      hours: t.durationMs >= 3_600_000,
    };
  }, [transcript.data, play.currentMs, play.pick, canPlay]);
  // A synthesis: its `[T#, mm:ss]` citations open the transcripts they name.
  const cites = useCitations(ws.document.id, ws.document.detectedKind === 'interview_synthesis');
  const citationTargets = useMemo(
    () => Object.fromEntries((cites.data?.citations ?? []).flatMap(c => (c.transcript ? [[c.label, c.transcript.id] as const] : []))),
    [cites.data],
  );
  const complete = async () => {
    if (await write.mutateAsync({ kind: 'complete' }).catch(() => null)) setView(2);
  };
  const doc = ws.document;
  const stepDone = (n: number) => (n === 1 && done) || (n === 2 && finished) || (n === 3 && checked);
  return (
    <div className="space-y-4">
      <Link to="/review" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> Review Inbox
      </Link>
      <PageHeader
        title={doc.filename}
        description={[
          ws.compare?.pending
            ? `Compared on ${ws.compare.routes.map(r => ROUTE_LABELS[r]).join(', ')}`
            : `${total} ${total === 1 ? 'entity' : 'entities'} and ${ws.claims} ${ws.claims === 1 ? 'claim' : 'claims'} found`,
          doc.profileSheet ? 'Actor Profile Sheet' : null,
          doc.sensitivity === 'confidential' ? 'Confidential' : null,
          !ws.compare?.pending && doc.extraction?.model ? `read by ${doc.extraction.model}` : null,
          // A thin result is often why: the usual AI route was down, or parts of the file could not be read.
          !ws.compare?.pending && doc.extraction?.fallback ? 'the usual AI route was unavailable, so a fallback read it' : null,
          !ws.compare?.pending && doc.extraction?.failedWindows
            ? `${doc.extraction.failedWindows} of ${doc.extraction.windows} parts could not be read`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      {ws.compare?.pending ? (
        <ComparePanel id={doc.id} />
      ) : (
        <>
          <nav aria-label="Review steps">
            <ol className="flex flex-wrap gap-2 text-sm">
              {REVIEW_STEPS.map(n => {
                const current = n === view;
                // Step 1 can always be opened; step 2 once the entity check is done; step 3 once the facts are written.
                const reachable = n === 1 || (n === 2 && done) || (n === 3 && finished);
                const inner = (
                  <>
                    <span className="grid size-5 place-items-center rounded-full bg-muted text-xs">
                      {stepDone(n) ? <CheckCheck className="size-3" aria-label="done" /> : n}
                    </span>
                    {REVIEW_STEP_LABELS[n]}
                  </>
                );
                return (
                  <li key={n} aria-current={current ? 'step' : undefined}>
                    {reachable && !current ? (
                      <button
                        type="button"
                        onClick={() => setView(n as 1 | 2 | 3)}
                        className="flex items-center gap-2 rounded-full border px-3 py-1 text-muted-foreground hover:text-foreground"
                      >
                        {inner}
                      </button>
                    ) : (
                      <span
                        className={cn(
                          'flex items-center gap-2 rounded-full border px-3 py-1',
                          current ? 'border-foreground font-medium' : 'text-muted-foreground',
                        )}
                      >
                        {inner}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
          {!ws.reviewable && (
            <p role="status" className="rounded-md border bg-muted px-3 py-2 text-sm">
              This document is no longer waiting for review. Decisions are shown as they were made.
            </p>
          )}
          {doc.sensitivity === 'confidential' && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="size-3" aria-hidden="true" /> Confidential: processed on Cloudflare only.
            </p>
          )}
          {view === 3 ? (
            <RecheckStep id={doc.id} filename={doc.filename} text={text} />
          ) : view === 2 ? (
            <>
              {done && !finished && ws.reviewable && (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-success-foreground" role="status">
                    Entities checked. Facts &amp; relations are next.
                  </p>
                  <span className="ms-auto">
                    <SkipReviewButton documentId={doc.id} onDone={() => navigate({ to: '/review' })} />
                  </span>
                </div>
              )}
              <FactsStep id={doc.id} snap={snap} text={text} onFinished={() => setView(3)} />
            </>
          ) : (
            <>
              <Tip id="review-step1" learnMore="review-an-upload">
                Decide what each entity is: an existing record, a new entity, or a duplicate of another one in this document. Nothing goes
                into the graph until the review is complete.
              </Tip>
              <section
                data-tour="review-progress"
                className="flex flex-wrap items-center gap-3 rounded-lg border bg-surface px-3 py-2"
                aria-label="Step 1 progress"
              >
                <span className="text-sm" data-testid="step1-progress">
                  <DataNumber>{total - pending}</DataNumber> of <DataNumber>{total}</DataNumber> entities decided
                </span>
                <progress
                  className="h-2 w-40 accent-foreground"
                  max={Math.max(total, 1)}
                  value={total - pending}
                  aria-label="Entities decided"
                />
                {ws.sameNameChecks > 0 && (
                  <span className="text-xs text-muted-foreground">
                    <DataNumber>{ws.sameNameChecks}</DataNumber> “Same person?” {ws.sameNameChecks === 1 ? 'check' : 'checks'} queued
                  </span>
                )}
                <div className="ms-auto flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate({ to: '/review' })}>
                    Save &amp; exit
                  </Button>
                  {ws.reviewable && !finished && <SkipReviewButton documentId={doc.id} onDone={() => navigate({ to: '/review' })} />}
                  {done ? (
                    <Button size="sm" onClick={() => setView(2)}>
                      Go to facts &amp; relations
                    </Button>
                  ) : (
                    <Button size="sm" disabled={!ws.reviewable || pending > 0 || write.isPending} onClick={complete}>
                      Continue to facts &amp; relations
                    </Button>
                  )}
                </div>
              </section>
              {write.error && (
                <p role="alert" className="text-sm text-danger-foreground">
                  {write.error}
                </p>
              )}
              <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div data-tour="review-source" className="flex max-h-[calc(100dvh-8rem)] min-h-80 flex-col gap-2 xl:sticky xl:top-0">
                  {canPlay && transcript.data && (
                    <TranscriptPlayer documentId={doc.id} audio={transcript.data.audio} onTime={play.setCurrentMs} handle={play.handle} />
                  )}
                  {text ? (
                    <SourceViewer
                      text={text.text}
                      truncated={text.truncated}
                      entities={viewerEntities}
                      selectedId={selected}
                      mentionIndex={mention}
                      onMentionIndex={setMention}
                      timeline={timeline}
                      citations={citationTargets}
                    />
                  ) : (
                    <div className="rounded-lg border bg-surface p-4 text-sm text-muted-foreground">
                      {textError || !doc.hasText
                        ? 'No text could be read from this file, so there is nothing to highlight.'
                        : 'Loading the text…'}
                    </div>
                  )}
                </div>
                <div data-tour="review-groups" className="space-y-4">
                  <DocumentSamePerson documentId={doc.id} snap={snap} editable={ws.reviewable && !finished} />
                  {transcript.data && (
                    <section aria-labelledby="review-speakers" className="space-y-2 rounded-lg border bg-surface p-3">
                      <h3 id="review-speakers" className="font-semibold">
                        Speakers
                      </h3>
                      <SpeakerMapping data={transcript.data} onJump={play.pick} />
                    </section>
                  )}
                  {ws.entities.length === 0 && (
                    <EmptyState icon={FileSearch} title="No entities found in this document" learnMore="what-ai-extracts">
                      The AI found no actors, events, places or assets here. The file stays searchable in the Library.
                    </EmptyState>
                  )}
                  {ws.groups.map(g => (
                    <Group
                      key={g.key}
                      group={g}
                      ws={ws}
                      byId={byId}
                      snap={snap}
                      selected={selected}
                      onSelect={select}
                      write={write}
                      by={ws.groupedBy}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Compare mode (task 20a): the comparison matrix until a run, or a pick of items, is adopted. */
function ComparePanel({ id }: { id: string }) {
  const cmp = useReviewComparison(id);
  const adopt = useAdopt(id);
  if (cmp.isError)
    return (
      <p role="alert" className="text-sm text-danger-foreground">
        {cmp.error instanceof ApiError ? cmp.error.message : 'The comparison could not be loaded. Try again in a moment.'}
      </p>
    );
  if (!cmp.data) return <p className="text-sm text-muted-foreground">Loading the comparison…</p>;
  return <CompareStep view={cmp.data} adopt={adopt} />;
}

/** Step 2 · Facts & relations: loads the document's claims with their drafts. */
function FactsStep({
  id,
  snap,
  text,
  onFinished,
}: {
  id: string;
  snap: SchemaSnapshot;
  text: { text: string; truncated: boolean } | null;
  onFinished: () => void;
}) {
  const claims = useReviewClaims(id, true);
  const write = useClaimWrite(id);
  if (claims.isError)
    return (
      <p role="alert" className="text-sm text-danger-foreground">
        {claims.error instanceof ApiError ? claims.error.message : 'The facts could not be loaded. Try again in a moment.'}
      </p>
    );
  if (!claims.data) return <p className="text-sm text-muted-foreground">Loading the facts…</p>;
  return <ClaimsStep view={claims.data} snap={snap} text={text} write={write} onFinished={onFinished} />;
}

/** Step 3 · AI re-check & conflicts: loads the document's findings and the state of the AI re-check. */
function RecheckStep({ id, filename, text }: { id: string; filename: string; text: { text: string; truncated: boolean } | null }) {
  const checks = useReviewChecks(id, true);
  const write = useCheckWrite(id);
  if (checks.isError)
    return (
      <p role="alert" className="text-sm text-danger-foreground">
        {checks.error instanceof ApiError ? checks.error.message : 'The checks could not be loaded. Try again in a moment.'}
      </p>
    );
  if (!checks.data) return <p className="text-sm text-muted-foreground">Loading the checks…</p>;
  return <ChecksStep view={checks.data} text={text} write={write} filename={filename} />;
}

function Group({
  group,
  ws,
  byId,
  snap,
  selected,
  onSelect,
  write,
  by,
}: {
  group: ReviewWorkspace['groups'][number];
  ws: ReviewWorkspace;
  byId: Map<string, ReviewCandidate>;
  snap: SchemaSnapshot;
  selected: string | null;
  onSelect: (id: string) => void;
  write: ReturnType<typeof useReviewWrite>;
  by: 'block' | 'section';
}) {
  const items = group.candidateIds.map(id => byId.get(id)).filter((c): c is ReviewCandidate => !!c);
  const pending = items.filter(c => c.decision === 'pending').length;
  const headingId = `group-${group.key.replace(/[^a-z0-9]+/gi, '-')}`;
  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={headingId} className="font-semibold">
          <DataText>{group.label}</DataText>
        </h3>
        <span className="text-xs text-muted-foreground">
          <DataNumber>{pending}</DataNumber> of <DataNumber>{items.length}</DataNumber> to decide
        </span>
        {pending > 0 && ws.reviewable && (
          <span className="ms-auto flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={write.isPending}
              onClick={() => write.mutateAsync({ kind: 'accept-suggested', section: group.key }).catch(() => {})}
              aria-label={`Accept suggested in ${group.label}`}
            >
              Accept suggested
            </Button>
            <FieldHelp
              label="Accept suggested"
              help={`For the undecided entities in this ${by === 'block' ? 'block' : 'section'}: pre-selected matches are accepted and entities with no likely existing match become new entities. Anything that may match an existing record is left for you to check.`}
              learnMore="review-an-upload"
            />
          </span>
        )}
      </div>
      <ul className="space-y-2">
        {items.map(c => (
          <CandidateCard key={c.id} c={c} ws={ws} snap={snap} expanded={selected === c.id} onToggle={() => onSelect(c.id)} write={write} />
        ))}
      </ul>
    </section>
  );
}
